/**
 * escalationDisplay — shared presentation for texting-agent escalations, used
 * by BOTH the console escalations tab (the full breakdown) and the lead page's
 * escalation banner (the compact headline). One source of truth so a flag reads
 * the same way wherever a recruiter meets it.
 *
 * The richness comes from the enriched payload the backend now stamps at flag
 * time (crmAgentScreening / crmAgentTurn): the lead's verbatim words, their
 * captured answer, the eligibility rule the answer broke, the AI's concern, and
 * the message the concern was found on so a click can jump straight to it.
 */
import { ReactNode } from 'react';
import { AlertTriangle, MessageSquareText, ClipboardCheck } from 'lucide-react';
import { prettySlug } from '../lib';

/** The service-owned jsonb an escalation carries (all fields optional). */
export interface AgentEscalationPayload {
  reason?: string | null;
  concern?: string | null;
  quote?: string | null;
  communication_id?: string | null;
  guidance?: string | null;
  release_at?: string | null;
  // §6.2 screening flags
  question_key?: string | null;
  question?: string | null;
  answer?: unknown;
  /** The lead's answer in WORDS when the raw value is an option key
   *  ("option_4" → "Heart attack"). Server-resolved from the question's
   *  options at flag time, and back-filled at read time for older flags;
   *  absent when the raw value already reads as itself. */
  answer_label?: string | null;
  /** What the agent TRIED to record when the questionnaire refused the value
   *  (`reason` above says why). Nothing was stored — never render this as the
   *  lead's captured answer. */
  attempted_answer?: unknown;
  verdict?: 'pass' | 'fail' | 'unknown' | null;
  rule?: string | null;
  derived?: unknown;
  // handoff rollup / operational causes
  rollup?: { passes?: number; fails?: number; unknowns?: number; flagged?: unknown[] } | null;
  cause?: string | null;
  [k: string]: unknown;
}

export interface AgentEscalationLike {
  id: string;
  kind: string;
  status: string;
  payload?: AgentEscalationPayload | null;
  created_at?: string;
  contact_id?: string | null;
  contact_name?: string | null;
  conversation_id?: string;
}

/** §3 kind → short human label (shared by the tab, the banner, the popup copy). */
export const ESCALATION_KIND_LABEL: Record<string, string> = {
  unknown_answer: 'Needs an answer',
  hostile_or_distress: 'Needs a human now',
  guardrail_block: 'Draft blocked',
  prescreen_flag: 'Screening concern',
  handoff_review: 'Screen complete',
  call_request: 'Call requested',
  human_requested: 'Person requested',
  bot_question: 'Asked if this is a bot',
  draft_wait: 'Draft waiting',
  dormant_handback: 'Handed back',
  error: 'Error',
  budget: 'Budget/kill',
};

/** Plain-language "what happened and what to do" per kind — the owner's
 *  "the queue is opaque without this" complaint. */
export const ESCALATION_KIND_GUIDE: Record<string, string> = {
  unknown_answer: "The lead asked something the study brief can't answer. Claim it and reply in the thread yourself. If a countdown is showing, the agent sends an honest “let me check with the team” itself when it hits zero.",
  hostile_or_distress: 'The lead sounds upset or in distress. The agent has gone quiet — a person should take this thread now.',
  guardrail_block: 'The agent wrote a reply that failed a safety check, so nothing was sent. Review it under Drafts — approving it there sends it on your authority.',
  prescreen_flag: 'A screening answer looks disqualifying or could not be confirmed. The lead was NOT told — screening continues; review the answer and the rule below.',
  handoff_review: 'Screening is complete and the lead was told the team will follow up. Review their answers and take the next step.',
  call_request: 'The lead asked for a phone call. Claim it, then call them or send a booking link from the lead page.',
  human_requested: 'The lead asked for a real person. Claim it and take over the thread.',
  bot_question: 'The lead asked whether this is a bot. Claim it and reply as yourself — never claim the earlier texts were written by a person. If the countdown ends unclaimed, the agent sends the honest automation disclosure.',
  draft_wait: 'A copilot draft has been waiting over 10 minutes. Approve, edit, or reject it under Drafts.',
  dormant_handback: 'The agent closed this conversation after silence. The lead is back in normal recruiter follow-up — nothing to undo.',
  error: 'Something in the agent pipeline failed; the conversation may be paused. Open the thread to see where it stopped.',
  budget: 'A spend cap or kill switch stopped the agent here. The conversation is paused until a person resumes or takes it.',
};

/**
 * Some `error` escalations have a specific, actionable story — "something in
 * the agent pipeline failed" tells a recruiter nothing when the real event is
 * that the lead answered and the questionnaire would not take the value.
 * Keyed on payload.cause; falls back to the kind guide.
 */
export const ESCALATION_CAUSE_GUIDE: Record<string, string> = {
  answer_rejected_by_form: 'The lead DID answer — the questionnaire would not accept the value, so nothing was recorded and the agent will ask again. Record the answer on their questionnaire yourself, or take the thread over.',
  form_backbone_mismatch: 'This conversation is holding a questionnaire record for a different form, so no answers can be saved. A person needs to take this thread.',
  questionnaire_no_visible_questions: 'This study’s questionnaire has nothing the agent can ask (every question is behind a branch that never opens). Fix the questionnaire, then re-engage.',
  template_unpublished: 'A fixed message this step needs has not been published yet, so the agent could not send it. Publish it under Settings → Prompts.',
  questionnaire_mint_failed: 'The agent could not create a questionnaire link for this study, so the message was not sent. Check the study has an active questionnaire.',
  determination_failed: 'Your determination could not be delivered to the lead. Nothing was sent — resubmit it, or text them yourself.',
  duplicate_of_last_sent: 'The agent was about to text this lead the exact same message twice in a row, so nothing was sent. That usually means it could not read their last reply — a picture text, or an answer too vague to record. Open the thread, look at what they actually sent, and reply yourself.',
};

/** The guide line for one escalation: cause-specific when we have one. */
export const escalationGuide = (e: AgentEscalationLike): string | null => {
  const cause = e.payload?.cause ? String(e.payload.cause) : null;
  return (cause && ESCALATION_CAUSE_GUIDE[cause]) || ESCALATION_KIND_GUIDE[e.kind] || null;
};

/** 'ps.sim.v1.tobacco_use' → 'Tobacco Use'. */
export const questionLabelFromKey = (key: string): string => prettySlug(key.split('.').pop() || key);

/**
 * The lead's captured answer → a short readable string.
 *
 * `label` (the server-resolved option wording) WINS when present: storage keeps
 * option values because eligibility rules compare against them, but a recruiter
 * reading a flag needs the words the lead picked, not "option_4".
 */
export function formatAnswer(value: unknown, label?: string | null): string {
  if (label) return label;
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return 'Declined to answer';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export type EscalationAnchor =
  | { kind: 'message'; commId: string }
  | { kind: 'screen'; questionKey: string }
  | null;

/**
 * Where "the concern was" lives, so a click can jump there. A screening flag
 * points at its criterion in the screen detail; anything carrying the message
 * it was found on points at that message in the thread.
 */
export function escalationAnchor(e: AgentEscalationLike): EscalationAnchor {
  const p = e.payload || {};
  if (e.kind === 'prescreen_flag' && p.question_key) {
    return { kind: 'screen', questionKey: String(p.question_key) };
  }
  if (p.communication_id) return { kind: 'message', commId: String(p.communication_id) };
  return null;
}

const VERDICT_CHIP: Record<string, string> = {
  fail: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  unknown: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  pass: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};
const VERDICT_WORD: Record<string, string> = { fail: 'may not qualify', unknown: 'needs review', pass: 'clear' };

/** The single most useful line of context under a kind label (banner + card). */
export function escalationConcern(e: AgentEscalationLike): string | null {
  const p = e.payload || {};
  if (e.kind === 'prescreen_flag') {
    const q = p.question || (p.question_key ? questionLabelFromKey(String(p.question_key)) : null);
    return q ? `“${q}” — ${VERDICT_WORD[p.verdict || 'unknown'] || 'needs review'}` : (p.concern || null);
  }
  if (e.kind === 'handoff_review' && p.rollup) {
    const r = p.rollup;
    const flagged = (r.fails || 0) + (r.unknowns || 0);
    return `${r.passes || 0} clear · ${flagged} to review`;
  }
  return p.concern || p.reason || null;
}

/**
 * The FULL breakdown for the escalations tab: verbatim words, the screening
 * question + answer + rule it broke, and the AI's concern. Renders nothing when
 * the payload carries no rich context (the card's guide line still shows).
 */
export function EscalationBreakdown({ e, answerLabel = null }: {
  e: AgentEscalationLike;
  /** Caller-supplied option wording, used when the payload predates the fix
   *  (the queue passes the live screen's label for this question). */
  answerLabel?: string | null;
}): JSX.Element | null {
  const p = e.payload || {};
  const isScreen = e.kind === 'prescreen_flag' || Boolean(p.question) || Boolean(p.question_key);
  const question = p.question || (p.question_key ? questionLabelFromKey(String(p.question_key)) : null);
  const verdict = (p.verdict as string) || null;
  const rule = p.rule || null;
  const quote = p.quote || null;
  const rejected = p.cause === 'answer_rejected_by_form';
  // The refusal reason has its own row below; showing it again as the AI's
  // concern would read as a second, different problem.
  const concern = p.concern || (rejected ? null : p.reason) || null;

  const rows: ReactNode[] = [];

  if (isScreen && question) {
    rows.push(
      <Row key="q" label="Question" icon={<ClipboardCheck className="h-3.5 w-3.5" />}>
        <span className="text-gray-800 dark:text-gray-200">{question}</span>
        {verdict && (
          <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${VERDICT_CHIP[verdict] || VERDICT_CHIP.unknown}`}>
            {VERDICT_WORD[verdict] || verdict}
          </span>
        )}
      </Row>,
    );
    // ONLY when the payload actually carries a captured answer. A payload with
    // no `answer` at all (every operational error escalation) used to render
    // "Declined to answer" — the exact opposite of what happened, on leads who
    // had answered plainly (owner-reported 2026-08-04).
    if ('answer' in p) {
      rows.push(
        <Row key="a" label="Their answer">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatAnswer(p.answer, p.answer_label || answerLabel)}
          </span>
        </Row>,
      );
    }
    if (rejected) {
      rows.push(
        <Row key="na" label="Not recorded">
          <span className="font-medium text-amber-700 dark:text-amber-400">
            {p.attempted_answer === undefined || p.attempted_answer === null || p.attempted_answer === ''
              ? 'their answer could not be saved to the questionnaire'
              : `the agent read this as “${formatAnswer(p.attempted_answer)}”, which the questionnaire refused`}
          </span>
          {p.reason && (
            <span className="block text-gray-600 dark:text-gray-400">{String(p.reason)}</span>
          )}
        </Row>,
      );
    }
    if (rule) {
      rows.push(
        <Row key="r" label="Rule">
          <span className="text-gray-700 dark:text-gray-300">This answer {rule}.</span>
        </Row>,
      );
    }
  }

  if (quote) {
    rows.push(
      <Row key="v" label="Lead said" icon={<MessageSquareText className="h-3.5 w-3.5" />}>
        <span className="italic text-gray-700 dark:text-gray-300">“{quote}”</span>
      </Row>,
    );
  }

  if (concern) {
    rows.push(
      <Row key="c" label="AI concern" icon={<AlertTriangle className="h-3.5 w-3.5" />}>
        <span className="text-gray-700 dark:text-gray-300">{concern}</span>
      </Row>,
    );
  }

  if (!rows.length) return null;
  return (
    <div className="mt-2 space-y-1 rounded-lg border border-gray-200 bg-gray-50/70 p-2.5 text-xs dark:border-gray-700 dark:bg-gray-900/30">
      {rows}
    </div>
  );
}

/**
 * One label/value line. Phones stack the label above the value — the fixed 24
 * (96px) label gutter left barely half a 375px screen for the answer, so every
 * quote and rule wrapped into a ragged column. From sm up it is the original
 * two-column row.
 */
function Row({ label, icon, children }: { label: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-2">
      <span className="inline-flex flex-shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 sm:mt-px sm:w-24 sm:text-xs">
        {icon}{label}
      </span>
      <span className="min-w-0 flex-1 break-words">{children}</span>
    </div>
  );
}
